import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import AuthContext from "../auth/AuthContext";
import { getInvitationInfo } from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

function RegisterPage() {
  const { auth, register } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const initialCode = useMemo(() => searchParams.get("code") || "", [searchParams]);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState(initialCode);
  const [mode, setMode] = useState("register"); // register | reset
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [infoLoading, setInfoLoading] = useState(Boolean(initialCode));

  const isReset = mode === "reset";
  useDocumentTitle(isReset ? "Sæt ny adgangskode" : "Opret konto");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!code.trim()) {
        setInfoLoading(false);
        setMode("register");
        return;
      }
      setInfoLoading(true);
      try {
        const info = await getInvitationInfo(code.trim());
        if (cancelled) return;
        setEmail(info.email || "");
        setUsername(info.username || "");
        setMode(info.mode === "reset" ? "reset" : "register");
      } catch {
        if (!cancelled) {
          setMode("register");
        }
      } finally {
        if (!cancelled) setInfoLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (auth?.user) {
    return <Navigate to="/profile" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const result = await register(username, email, password, code);
      const wasReset = result?.reset || isReset;
      setSuccess(
        wasReset
          ? "Ny adgangskode gemt. Du kan nu logge ind."
          : "Registration complete. You can now sign in."
      );
      setPassword("");
      if (!wasReset) {
        setUsername("");
        setEmail("");
        setCode("");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Registration failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        bgcolor: "background.default",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: "100%",
          p: { xs: 3, sm: 4 },
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ textAlign: "center", fontWeight: 700, mb: 3 }}
        >
          {isReset ? "Sæt ny adgangskode" : "Opret konto"}
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? <Alert severity="success">{success}</Alert> : null}
            {isReset ? (
              <Typography variant="body2" color="text.secondary">
                Vælg en ny adgangskode til {username || "din konto"}. Dit brugernavn bevares.
              </Typography>
            ) : null}
            {!isReset ? (
              <TextField
                label="Brugernavn"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                fullWidth
                disabled={infoLoading}
              />
            ) : null}
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              disabled={infoLoading || isReset}
              InputProps={isReset ? { readOnly: true } : undefined}
            />
            <TextField
              label={isReset ? "Ny adgangskode" : "Adgangskode"}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              autoComplete={isReset ? "new-password" : "new-password"}
            />
            <TextField
              label="Invitationskode"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              fullWidth
              disabled={Boolean(initialCode) && isReset}
            />
            <Button type="submit" variant="contained" color="primary" size="large" fullWidth disabled={submitting || infoLoading}>
              {submitting
                ? isReset
                  ? "Gemmer…"
                  : "Opretter konto…"
                : isReset
                  ? "Gem ny adgangskode"
                  : "Opret konto"}
            </Button>
            <Typography variant="body2" sx={{ textAlign: "center" }}>
              Har du allerede en konto? <Link to="/login">Log ind</Link>
            </Typography>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

export default RegisterPage;
